// stores/translation.ts - 翻译任务状态管理 Pinia Store
// Phase3 集成计划（不改变现有模拟行为，添加集成占位与样板调用）：
// - 新增 uploadAndCreateTask：先上传文件获取 fileId，再 createTask
// - 新增 startPolling/stopPolling：轮询任务状态（或未来替换为推送）
// - 新增 cancelTask/retryTask：任务操作占位

import { defineStore } from 'pinia'
import { ElMessage } from 'element-plus'
import type { Task, LanguageInfo, Int64Id } from '~/types/translation'
import { TranslateStatus } from '~/types/translation'
import { useUploadApi } from '~/composables/useUploadApi'
import { useTranslationApi } from '~/composables/useTranslationApi'
import { useCfgManagerApi } from '~/composables/useCfgManagerApi'
import { useUserStore } from '~/stores/user'
import { useMyPageStore } from '~/stores/myPage'
import { useCfgStorage } from '~/composables/useCfgStorage'
import { normalizeInt64IdOrThrow, toInt64MapKey } from '~/utils/int64Id.js'
import {
  FUNNEL_EVENTS,
  buildFileEventParams,
  buildTranslationTerminalDedupeKey,
  trackFunnelEvent
} from '~/utils/gaFunnelEvents.js'

// 简单的 UUID 生成函数（回退方案）
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

// 轮询定时器：recordId -> timerId（浏览器 setTimeout 返回 number）
const pollTimers: Map<string, number> = new Map()

function trackTranslationTerminalEvent(task: Task | undefined, recordId: Int64Id, eventName: string) {
  trackFunnelEvent(
    eventName,
    buildFileEventParams(task?.originalFileName, { record_id: recordId }),
    { dedupeKey: buildTranslationTerminalDedupeKey(recordId) }
  )
}

// ==================== Store定义 ====================

export const useTranslationStore = defineStore('translation', {
  state: () => ({
    // 任务列表
    tasks: [] as Task[],

    // 源语言列表
    srcLanguageList: [] as LanguageInfo[],

    // 目标语言列表
    dstLanguageList: [] as LanguageInfo[],

    // 默认源语言（由服务端配置提供，初始为空以避免闪动）
    defaultSrc: '',

    // 默认目标语言（由服务端配置提供，初始为空以避免闪动）
    defaultDst: '',

    // 是否已初始化
    initialized: false
  }),

  getters: {
    // 进行中的任务
    activeTasks: (state): Task[] => {
      return state.tasks.filter(t => t.status === 'uploading' || t.status === 'translating')
    },

    // 已完成的任务
    completedTasks: (state): Task[] => {
      return state.tasks.filter(t => t.status === 'completed')
    },

    // 失败的任务
    failedTasks: (state): Task[] => {
      return state.tasks.filter(t => t.status === 'failed')
    },

    // 所有任务数量
    taskCount: (state): number => {
      return state.tasks.length
    },

    // 活跃任务数量
    activeTaskCount: (state): number => {
      return state.tasks.filter(t => t.status === 'uploading' || t.status === 'translating').length
    }
  },

  actions: {
    // ==================== 初始化 ====================

    /**
     * 初始化翻译模块
     * 加载语言列表和默认值
     * 第2阶段：使用硬编码的语言列表
     * 第3阶段：优先从配置中心加载，可回退硬编码
     */
    async initialize() {
      if (this.initialized) {
        return
      }

      // 模拟延迟
      await new Promise(resolve => setTimeout(resolve, 300))

      // 尝试从配置中心加载语言配置
      try {
        const cfgApi = useCfgManagerApi()
        const cache = useCfgStorage()
        const { latestCfgVersion, list } = await cfgApi.listCfgKey()
        const keyVersionMap = Object.fromEntries(list.map(x => [x.key, x.version])) as Record<string, number>
        cache.invalidateIfVersionChanged(latestCfgVersion, keyVersionMap)

        const cached = cache.getItem('doc_translate_languages')
        let cfgValue: any | null = cached?.value ? JSON.parse(cached.value) : null

        if (!cfgValue) {
          const got = await cfgApi.getCfg(['doc_translate_languages'])
          const item = got.find(x => x.key === 'doc_translate_languages')
          if (!item) throw new Error('配置缺失: doc_translate_languages')
          const ver = (list.find(x => x.key === 'doc_translate_languages')?.version) || latestCfgVersion
          cache.setItem('doc_translate_languages', ver, item.value)
          cfgValue = JSON.parse(item.value)
        }

        // 映射为前端结构
        const fromList: LanguageInfo[] = (cfgValue.from || []).map((x: any) => ({ code: x.code, name: x.name, section: x.section || '' }))
        const toList: LanguageInfo[] = (cfgValue.to || []).map((x: any) => ({ code: x.code, name: x.name, section: x.section || '' }))
        const defFrom: string = cfgValue.default_from || 'auto'
        const defTo: string = cfgValue.default_to || 'zh'

        if (!Array.isArray(fromList) || !Array.isArray(toList)) throw new Error('配置格式错误: 语言列表')

        this.srcLanguageList = fromList
        this.dstLanguageList = toList
        this.defaultSrc = defFrom
        this.defaultDst = defTo
      } catch (e: any) {
        // 不做兜底列表，交给服务端提供；仅提示（详细错误由拦截器提示）
        console.warn('加载语言配置失败:', e)
        ElMessage && ElMessage.warning('加载语言配置失败，请重试')
      }

      // 标记为已初始化
      this.initialized = true
    },

    // ==================== 任务管理 ====================

    /**
     * 创建翻译任务 (旧：包含上传；新：主要保留兼容，后续建议拆分使用)
     * 此方法现在主要用于不需要分步处理的场景，或者作为便利方法
     */
    async createTranslationTask(
      file: File,
      targetLanguage: string,
      sourceLanguage: string = 'auto',
      category: string = 'general'
    ): Promise<string> {
      // 生成唯一任务ID
      const taskId = generateUUID()

      // 1) 插入占位任务，进入“上传中”
      const newTask: Task = {
        id: taskId,
        originalFileName: file.name,
        targetLanguage,
        sourceLanguage,
        category,
        status: 'uploading',
        progress: 0,
        createdAt: new Date().toISOString()
      }
      this.tasks.unshift(newTask)

      try {
        // 2) 上传文件
        const { uploadFile } = useUploadApi()
        let lastProgress = 0
        const up = await uploadFile(file, {
          onProgress: (p) => {
            if (p !== lastProgress) {
              lastProgress = p
              this.updateTaskStatus(taskId, 'uploading', { progress: p })
            }
          }
        })
        
        // 保存 fileId 到任务中，以便重试
        const t = this.getTaskById(taskId)
        if (t) t.fileId = up.fileId

        // 3) 调用后端创建翻译任务
        const { createTask } = useTranslationApi()
        const { recordId } = await createTask({ fileId: up.fileId, from: sourceLanguage, to: targetLanguage })

        if (t) t.recordId = recordId
        this.updateTaskStatus(taskId, 'translating')

        try {
          const userStore = useUserStore()
          await userStore.fetchUserInfoFromUserMix()
        } catch {}

        // 4) 启动轮询
        this.startPolling(recordId, taskId)
        return taskId
      } catch (e: any) {
        const msg = e?.message || '创建翻译任务失败'
        this.updateTaskStatus(taskId, 'failed', { error: msg })
        throw e
      }
    },

    /**
     * 使用已上传的 fileId 创建翻译任务 (新交互流程的第二步)
     */
    async createTaskWithFileId(
      fileId: Int64Id,
      fileName: string,
      targetLanguage: string,
      sourceLanguage: string = 'auto',
      category: string = 'general'
    ): Promise<string> {
      const normalizedFileId = normalizeInt64IdOrThrow(fileId, 'fileId')
      const taskId = generateUUID()

      // 直接进入“翻译中”状态的任务（或者先显示排队，这里简化为 translating）
      const newTask: Task = {
        id: taskId,
        fileId: normalizedFileId,
        originalFileName: fileName,
        targetLanguage,
        sourceLanguage,
        category,
        status: 'translating',
        progress: 0,
        createdAt: new Date().toISOString()
      }
      this.tasks.unshift(newTask)

      try {
        const { createTask } = useTranslationApi()
        const { recordId } = await createTask({ fileId: normalizedFileId, from: sourceLanguage, to: targetLanguage })

        const t = this.getTaskById(taskId)
        if (t) t.recordId = recordId
        
        try {
          const userStore = useUserStore()
          await userStore.fetchUserInfoFromUserMix()
        } catch {}

        this.startPolling(recordId, taskId)
        return taskId
      } catch (e: any) {
        const msg = e?.message || '创建翻译任务失败'
        this.updateTaskStatus(taskId, 'failed', { error: msg })
        throw e
      }
    },

    /**
     * Phase3：上传并创建翻译任务（占位实现）
     */
    async uploadAndCreateTask(file: File, targetLanguage: string, sourceLanguage: string = 'auto') {
      console.warn('[TODO] Phase3 uploadAndCreateTask: hook real upload + createTask here')
    },

    /**
     * 更新任务状态
     */
    updateTaskStatus(
      taskId: string,
      status: Task['status'],
      data?: { downloadUrl?: string; error?: string; progress?: number }
    ) {
      const task = this.tasks.find(t => t.id === taskId)

      if (!task) {
        console.warn(`Task not found: ${taskId}`)
        return
      }

      task.status = status

      if (status === 'completed' && data?.downloadUrl) {
        task.downloadUrl = data.downloadUrl
        task.progress = 100
      } else if (status === 'failed' && data?.error) {
        task.error = data.error
      } else if (data?.progress !== undefined) {
        task.progress = data.progress
      }
    },

    /**
     * 根据ID查找任务
     */
    getTaskById(taskId: string): Task | undefined {
      return this.tasks.find(t => t.id === taskId)
    },

    /**
     * 清除所有任务（用于登出时）
     */
    clearAllTasks() {
      this.tasks = []
    },


    // ==================== 模拟逻辑（第2阶段） ====================

    /**
     * 模拟任务进度变化
     */
    simulateTaskProgress(taskId: string) {
      const task = this.getTaskById(taskId)
      if (!task) {
        return
      }

      if (task.status === 'uploading') {
        setTimeout(() => {
          this.updateTaskStatus(taskId, 'translating', { progress: 30 })
          this.simulateTaskProgress(taskId)
        }, 2000)
      } else if (task.status === 'translating') {
        setTimeout(() => {
          const isSuccess = Math.random() < 0.9 

          if (isSuccess) {
            this.updateTaskStatus(taskId, 'completed', {
              downloadUrl: `https://example.com/download/${taskId}`,
              progress: 100
            })
          } else {
            this.updateTaskStatus(taskId, 'failed', {
              error: '文件格式不支持，请检查文件完整性'
            })
          }
        }, 5000)
      }
    },

    // ==================== Phase3 轮询与操作占位 ====================
    
    /** 开始轮询某个任务的状态（真实实现） */
    startPolling(recordId: Int64Id, taskId: string) {
      const api = useTranslationApi()
      const normalizedRecordId = normalizeInt64IdOrThrow(recordId, 'recordId')
      const recordIdKey = toInt64MapKey(normalizedRecordId)

      const schedule = (delayMs: number) => {
        const timerId = window.setTimeout(async () => {
          try {
            const r = await api.queryTaskStatus(normalizedRecordId)
            if (!r.isEnd) {
              // 处理中：从服务端响应中读取进度并更新
              this.updateTaskStatus(taskId, 'translating', { progress: r.progressPercent })
              const next = Math.max(1000, r.pollIntervalMs || 0)
              schedule(next)
            } else {
              // 已结束：根据结果设置完成/失败并停止轮询
              if (r.status === TranslateStatus.Success) {
                this.updateTaskStatus(taskId, 'completed', { downloadUrl: r.downloadUrl })
                trackTranslationTerminalEvent(this.getTaskById(taskId), normalizedRecordId, FUNNEL_EVENTS.TRANSLATION_SUCCESS)
              } else {
                this.updateTaskStatus(taskId, 'failed', { error: '翻译失败' })
                trackTranslationTerminalEvent(this.getTaskById(taskId), normalizedRecordId, FUNNEL_EVENTS.TRANSLATION_FAIL)
              }
              this.stopPolling(normalizedRecordId)

              // 任务结束后再次刷新用户信息/配额（方案B）
              try {
                const userStore = useUserStore()
                await userStore.fetchUserInfoFromUserMix()
              } catch {}
            }
          } catch (err: any) {
            // 错误已在拦截器提示，这里标记失败并停止轮询
            console.error('查询任务状态失败:', err)
            this.updateTaskStatus(taskId, 'failed', { error: err?.message || '查询失败' })
            trackTranslationTerminalEvent(this.getTaskById(taskId), normalizedRecordId, FUNNEL_EVENTS.TRANSLATION_FAIL)
            this.stopPolling(normalizedRecordId)
          }
        }, Math.max(0, delayMs))
        pollTimers.set(recordIdKey, timerId)
      }

      // 立即触发一次
      schedule(0)
    },

    /** 停止轮询（真实实现） */
    stopPolling(recordId: Int64Id) {
      const normalizedRecordId = normalizeInt64IdOrThrow(recordId, 'recordId')
      const recordIdKey = toInt64MapKey(normalizedRecordId)
      const timerId = pollTimers.get(recordIdKey)
      if (timerId) {
        clearTimeout(timerId)
        pollTimers.delete(recordIdKey)
      }
    },

    /** 停止所有轮询（登出/页面卸载时串联清理） */
    stopAllPolling() {
      for (const [rid, timerId] of Array.from(pollTimers.entries())) {
        clearTimeout(timerId)
        pollTimers.delete(rid)
      }
    },

    /** 取消任务（占位） */
    async cancelTask(recordId: Int64Id) {
      normalizeInt64IdOrThrow(recordId, 'recordId')
      // TODO(Phase3): await useTranslationApi().cancelTask(recordId)
      console.warn('[TODO] Phase3 cancelTask')
    },

    /** 
     * 重新翻译
     * 如果任务中有 fileId，则直接复用创建新任务（无需重传）
     */
    async retryTask(taskId: string) {
      const task = this.getTaskById(taskId)
      if (!task) return

      // 必须有 fileId 才能重试翻译（否则需要走 UI 层的重新上传流程）
      if (!task.fileId) {
        ElMessage.warning('原文件信息丢失，无法直接重试，请重新上传')
        return
      }

      // 重置状态
      this.updateTaskStatus(taskId, 'translating', { error: undefined })

      try {
        const { createTask } = useTranslationApi()
        const { recordId } = await createTask({ 
          fileId: task.fileId, 
          from: task.sourceLanguage || 'auto', 
          to: task.targetLanguage 
        })

        // 更新 recordId
        task.recordId = recordId

        // 重启轮询
        this.startPolling(recordId, taskId)
        
        ElMessage.success('已开始重试任务')
      } catch (e: any) {
        const msg = e?.message || '重试失败'
        this.updateTaskStatus(taskId, 'failed', { error: msg })
      }
    }
  }
})
